import React, { useState, useEffect } from 'react';
import { toViewerZone, viewerTz, aptTimeLabel } from '../utils/timeZone';
import { ParticipantSeesLine } from '../components/ParticipantSeesLine';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { CaseAppointmentRecurring, AppointmentCaseManagerSelect } from '../components/CaseAppointmentFields';
import { AppointmentEditDialog } from '../components/AppointmentEditDialog';
import { formatDate as formatDateUtil, formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogBody,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  ArrowLeft, 
  Edit, 
  FileText, 
  Plus,
  Loader2,
  User,
  Calendar,
  Target,
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
  MessageSquare,
  MapPin,
  Building,
  Briefcase,
  Trash2,
  AlertTriangle,
  Eye,
  UserCog,
  Zap,
  ChevronDown,
  AlertCircle,
  MessageCircle,
  CheckCheck,
  Pin,
  PinOff,
  CalendarDays,
  Phone,
  Video,
  RefreshCw,
  Ban,
  ArrowUp,
  ArrowDown,
  Pencil
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import CommunicationsPanel from '../components/CommunicationsPanel';
import CaseDocumentsPanel from '../components/CaseDocumentsPanel';
import SnapEtTargetedOutcomes from '../components/SnapEtTargetedOutcomes';
import SnapEtProgramReportCard from '../components/SnapEtProgramReportCard';
import HouseholdDetailsCard from '../components/HouseholdDetailsCard';
import ParticipantCostsPanel from '../components/ParticipantCostsPanel';
import EmploymentOutcomeCard from '../components/EmploymentOutcomeCard';
import AddictionsScreeningInstrumentCard from '../components/AddictionsScreeningInstrumentCard';
import EnrollmentSummaryQuestionsDialog from '../components/EnrollmentSummaryQuestionsDialog';
import { SupportiveServiceTicketsTab } from '../components/SupportiveServiceTicketsTab';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Roles that can assign cases to other users
const ASSIGN_ROLES = ['system_admin', 'executive', 'operations_manager'];

// Roles that can view participant costs
const PARTICIPANT_COSTS_ROLES = ['system_admin', 'executive', 'operations_manager'];

// Programs that should not show "Assigned To" card
const UNASSIGNED_PROGRAMS = ['food pantry', 'addictions recovery'];

// Helper function to format date-only strings without timezone conversion
// This prevents dates like "2026-07-16" from being shifted back a day due to UTC interpretation
const formatDateOnly = (dateStr) => {
  if (!dateStr) return 'N/A';
  return formatDateUtil(dateStr) || 'N/A';
};

const CaseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const [caseData, setCaseData] = useState(null);
  const [participant, setParticipant] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [closeExitOutcome, setCloseExitOutcome] = useState('');
  const [closing, setClosing] = useState(false);

  // Exit Outcome options for case close
  const EXIT_OUTCOME_OPTIONS = [
    'Currently Enrolled',
    'Successful Completion',
    'Successful Completion - Employed',
    'Successful Completion - Loss of Benefits',
    'Exited - Unsuccessful Completion',
    'Exited - Withdrawn',
    'Exited - Loss of Contact',
    'Internal Error'
  ];
  
  // View note state
  const [viewNoteDialogOpen, setViewNoteDialogOpen] = useState(false);
  const [noteToView, setNoteToView] = useState(null);
  
  // Delete note state
  const [deleteNoteDialogOpen, setDeleteNoteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deletingNote, setDeletingNote] = useState(false);
  
  // Case assignment state
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [assigning, setAssigning] = useState(false);
  
  // Quick Note state (Food Pantry only)
  const [quickNoteTemplates, setQuickNoteTemplates] = useState([]);
  const [creatingQuickNote, setCreatingQuickNote] = useState(false);
  
  // MTAJ-NC Quick Note state
  const [mtajQuickNoteTemplates, setMtajQuickNoteTemplates] = useState([]);
  const [mtajReverseReferralDialogOpen, setMtajReverseReferralDialogOpen] = useState(false);
  const [mtajOutcome, setMtajOutcome] = useState('');
  const [creatingMtajQuickNote, setCreatingMtajQuickNote] = useState(false);
  
  // SNAP Outreach NC Quick Note state
  const [snapOutreachTemplates, setSnapOutreachTemplates] = useState([]);
  const [creatingSnapOutreachNote, setCreatingSnapOutreachNote] = useState(false);
  
  // Addictions Recovery Program Quick Note state
  const [arpQuickNoteTemplates, setArpQuickNoteTemplates] = useState([]);
  const [creatingArpQuickNote, setCreatingArpQuickNote] = useState(false);
  const [customQuickNotes, setCustomQuickNotes] = useState([]);
  const [creatingCustomQuickNote, setCreatingCustomQuickNote] = useState(false);
  const [pendingSummaryRun, setPendingSummaryRun] = useState(null);
  
  // Employment Outcome & Retention integration state
  const [employmentOutcomeComplete, setEmploymentOutcomeComplete] = useState(false);
  const [pendingRetentionEnable, setPendingRetentionEnable] = useState(false);
  const [employmentOutcomeRef, setEmploymentOutcomeRef] = useState(null);
  
  // Review Note state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [noteToReview, setNoteToReview] = useState(null);
  const [reviewComment, setReviewComment] = useState('');
  const [requestingReview, setRequestingReview] = useState(false);
  
  // Correction submission state
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [noteToCorrected, setNoteToCorrected] = useState(null);
  const [correctionNotes, setCorrectionNotes] = useState('');
  const [submittingCorrection, setSubmittingCorrection] = useState(false);
  
  // Check if user can assign cases
  const canAssignCases = ASSIGN_ROLES.includes(user?.role);
  
  // Check if user can request reviews (Sys Admin, Executive, Operations Manager)
  const canRequestReview = ['system_admin', 'executive', 'executive_leadership', 'operations_manager'].includes(user?.role);
  
  // Check if user can highlight/unpin case notes (Sys Admin, Executive, Operations Manager)
  const canHighlightNotes = ['system_admin', 'executive', 'operations_manager'].includes(user?.role);
  
  // State for highlight operations
  const [highlightLoading, setHighlightLoading] = useState(null);
  
  // Appointments state
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [showNewAppointmentDialog, setShowNewAppointmentDialog] = useState(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [appointmentToReschedule, setAppointmentToReschedule] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [updatingAppointment, setUpdatingAppointment] = useState(null);
  const [newAppointment, setNewAppointment] = useState({
    title: '',
    appointment_type: '',
    date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '09:30',
    location: '',
    location_type: 'in_person',
    notes: '',
    case_manager_id: null,
    is_recurring: false,
    recurrence_interval: 7,
    recurrence_unit: 'days',
    recurrence_end_date: ''
  });
  const [savingAppointment, setSavingAppointment] = useState(false);
  
  // Check if this is a Food Pantry case
  const isFoodPantryCase = caseData?.program_type?.toLowerCase().includes('food_pantry') || 
                           caseData?.program_name?.toLowerCase().includes('food pantry');
  
  // Check if this is an MTAJ-NC Referral case
  const isMtajNcCase = caseData?.program_type?.toLowerCase().includes('mtaj') ||
                       caseData?.program_name?.toLowerCase().includes('mtaj');
  
  // Check if this is a SNAP Outreach NC case
  const isSnapOutreachNcCase = caseData?.service_type?.toLowerCase().includes('snap outreach program - north carolina') ||
                               caseData?.program_type?.toLowerCase().includes('snap_outreach_nc') ||
                               (caseData?.program_name?.toLowerCase().includes('snap outreach') && 
                                caseData?.state?.toUpperCase() === 'NC');
  
  // Check if this is a SNAP E&T case (for Participant Costs tab)
  const isSnapEtCase = caseData?.program_name?.toLowerCase().includes('snap') && 
                       caseData?.program_name?.toLowerCase().includes('e&t') ||
                       caseData?.case_type?.toLowerCase().includes('snap_et') ||
                       caseData?.enrollment_type_id?.toLowerCase().includes('snap_et');
  
  // Check if this is an Addictions Recovery Program case
  const isAddictionsRecoveryCase = caseData?.program_name?.toLowerCase().includes('addictions') ||
                                   caseData?.program_type?.toLowerCase().includes('addictions');
  
  // Check if this is a Community Service case
  const isCommunityServiceCase = caseData?.program_name?.toLowerCase().includes('community service') ||
                                  caseData?.program_type?.toLowerCase().includes('community_service');
  
  // Community Service Tracker state
  const [communityServiceTracker, setCommunityServiceTracker] = useState(null);
  const [loadingCommunityService, setLoadingCommunityService] = useState(false);
  const [savingCommunityService, setSavingCommunityService] = useState(false);
  const [communityServiceForm, setCommunityServiceForm] = useState({
    service_type: '',
    service_type_other: '',
    verification_required: null,
    court_order_on_file: null,
    required_hours: 0
  });
  const [hoursLogForm, setHoursLogForm] = useState({ date: '', hours: '' });
  const [loggingHours, setLoggingHours] = useState(false);
  const [hoursSortOrder, setHoursSortOrder] = useState('desc');

  // Check if user can view participant costs
  const canViewParticipantCosts = PARTICIPANT_COSTS_ROLES.includes(user?.role);
  
  // Get navigation state for context preservation
  const navigationState = location.state || {};
  const defaultTab = navigationState.activeTab || new URLSearchParams(location.search).get('tab') || 'overview';

  useEffect(() => {
    fetchCaseData();
  }, [id]);

  const fetchCaseData = async () => {
    try {
      const [caseRes, notesRes] = await Promise.all([
        axios.get(`${API}/cases/${id}`),
        axios.get(`${API}/case-notes?case_id=${id}`)
      ]);
      setCaseData(caseRes.data);
      setNotes(notesRes.data.items);
      
      // Fetch participant details for communications
      if (caseRes.data.participant_id) {
        const participantRes = await axios.get(`${API}/participants/${caseRes.data.participant_id}`);
        setParticipant(participantRes.data);
      }
    } catch (error) {
      toast.error('Failed to load case');
      navigate('/cases');
    } finally {
      setLoading(false);
    }
  };

  // Fetch appointments for this case
  const fetchAppointments = async () => {
    setLoadingAppointments(true);
    try {
      const response = await axios.get(`${API}/cases/${id}/appointments`);
      setAppointments((response.data.appointments || []).map(a => toViewerZone(a, viewerTz(user))));
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    } finally {
      setLoadingAppointments(false);
    }
  };

  // Fetch appointment types
  const fetchAppointmentTypes = async () => {
    try {
      const response = await axios.get(`${API}/appointment-types`);
      setAppointmentTypes(response.data.appointment_types || []);
    } catch (error) {
      console.error('Failed to fetch appointment types:', error);
    }
  };

  // Load appointments when case data is available
  useEffect(() => {
    if (caseData?.id) {
      fetchAppointments();
      fetchAppointmentTypes();
      fetchStaffList();
    }
  }, [caseData?.id]);

  const openNewAppointmentDialog = () => {
    setNewAppointment(prev => ({ ...prev, case_manager_id: caseData?.assigned_staff_id || null }));
    setShowNewAppointmentDialog(true);
  };

  const handleAppointmentCaseManager = async (apt, staffId) => {
    setUpdatingAppointment(apt.id);
    try {
      const res = await axios.put(`${API}/cases/${id}/appointments/${apt.id}/case-manager`, { case_manager_id: staffId });
      toast.success(res.data.case_manager_name ? `Appointment assigned to ${res.data.case_manager_name}` : 'Case manager removed from appointment');
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change case manager');
    } finally {
      setUpdatingAppointment(null);
    }
  };

  // Create new appointment
  const handleCreateAppointment = async () => {
    if (!newAppointment.title || !newAppointment.date || !newAppointment.start_time) {
      toast.error('Please fill in required fields');
      return;
    }

    if (newAppointment.is_recurring && !newAppointment.recurrence_end_date) {
      toast.error('Please set an end date for the recurring appointment');
      return;
    }

    setSavingAppointment(true);
    try {
      const res = await axios.post(`${API}/cases/${id}/appointments`, newAppointment);
      toast.success(res.data?.series_id ? `Created ${res.data.occurrences_created} recurring appointments` : 'Appointment created');
      setShowNewAppointmentDialog(false);
      setNewAppointment({
        title: '',
        appointment_type: '',
        date: new Date().toISOString().split('T')[0],
        start_time: '09:00',
        end_time: '09:30',
        location: '',
        location_type: 'in_person',
        notes: '',
        case_manager_id: null,
        is_recurring: false,
        recurrence_interval: 7,
        recurrence_unit: 'days',
        recurrence_end_date: ''
      });
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create appointment');
    } finally {
      setSavingAppointment(false);
    }
  };

  // Update appointment status (Reschedule, Cancel, No Show)
  const handleUpdateAppointmentStatus = async (appointment, status, rescheduleData = {}) => {
    setUpdatingAppointment(appointment.id);
    try {
      const payload = { status, ...rescheduleData };
      const response = await axios.put(
        `${API}/cases/${id}/appointments/${appointment.id}/status`,
        payload
      );
      
      if (status === 'Rescheduled' && response.data.new_appointment_id) {
        toast.success(`Appointment rescheduled. New appointment created.`);
      } else {
        toast.success(`Appointment marked as ${status}`);
      }
      
      setShowRescheduleDialog(false);
      setAppointmentToReschedule(null);
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update appointment');
    } finally {
      setUpdatingAppointment(null);
    }
  };

  // Open reschedule dialog
  const openRescheduleDialog = (appointment) => {
    setAppointmentToReschedule(appointment);
    // Default to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setRescheduleDate(tomorrow.toISOString().split('T')[0]);
    setRescheduleTime(appointment.start_time || '09:00');
    setShowRescheduleDialog(true);
  };

  // Handle appointment type change for auto-fill
  const handleAppointmentTypeChange = (typeId) => {
    const type = appointmentTypes.find(t => t.id === typeId);
    if (type) {
      setNewAppointment(prev => {
        const [hours, minutes] = prev.start_time.split(':').map(Number);
        const startMinutes = hours * 60 + minutes;
        const endMinutes = startMinutes + (type.duration || 30);
        const endHours = Math.floor(endMinutes / 60);
        const endMins = endMinutes % 60;
        
        return {
          ...prev,
          appointment_type: typeId,
          title: prev.title || type.label || type.name,
          is_recurring: type.triggers_snap_et_reminder ? prev.is_recurring : false,
          end_time: `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`
        };
      });
    }
  };

  // Get status badge style
  const getAppointmentStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case 'scheduled': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'attended': case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'rescheduled': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'no show': case 'no_show': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'cancelled': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Location type icons
  const getLocationIcon = (locationType) => {
    switch (locationType) {
      case 'phone': return <Phone className="w-3 h-3" />;
      case 'video': return <Video className="w-3 h-3" />;
      default: return <MapPin className="w-3 h-3" />;
    }
  };

  // Fetch Community Service Tracker data
  const fetchCommunityServiceTracker = async () => {
    if (!isCommunityServiceCase) return;
    setLoadingCommunityService(true);
    try {
      const response = await axios.get(`${API}/cases/${id}/community-service-tracker`);
      setCommunityServiceTracker(response.data);
      setCommunityServiceForm({
        service_type: response.data.service_type || '',
        service_type_other: response.data.service_type_other || '',
        verification_required: response.data.verification_required,
        court_order_on_file: response.data.court_order_on_file,
        required_hours: response.data.required_hours || 0
      });
    } catch (error) {
      console.error('Failed to fetch community service tracker:', error);
    } finally {
      setLoadingCommunityService(false);
    }
  };

  // Save Community Service Tracker settings
  const saveCommunityServiceTracker = async () => {
    setSavingCommunityService(true);
    try {
      await axios.put(`${API}/cases/${id}/community-service-tracker`, communityServiceForm);
      toast.success('Community service tracker updated');
      fetchCommunityServiceTracker();
    } catch (error) {
      toast.error('Failed to update tracker');
    } finally {
      setSavingCommunityService(false);
    }
  };

  // Log Community Service Hours
  const logCommunityServiceHours = async () => {
    if (!hoursLogForm.date || !hoursLogForm.hours) {
      toast.error('Please enter date and hours');
      return;
    }
    setLoggingHours(true);
    try {
      await axios.post(`${API}/cases/${id}/community-service-tracker/hours`, hoursLogForm);
      toast.success('Hours logged successfully');
      setHoursLogForm({ date: '', hours: '' });
      fetchCommunityServiceTracker();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log hours');
    } finally {
      setLoggingHours(false);
    }
  };

  // Delete Community Service Hours entry
  const deleteCommunityServiceHours = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this hours entry?')) return;
    try {
      await axios.delete(`${API}/cases/${id}/community-service-tracker/hours/${entryId}`);
      toast.success('Hours entry deleted');
      fetchCommunityServiceTracker();
    } catch (error) {
      toast.error('Failed to delete hours entry');
    }
  };

  // Get sorted hours entries
  const getSortedHoursEntries = () => {
    if (!communityServiceTracker?.hours_entries) return [];
    const entries = [...communityServiceTracker.hours_entries];
    entries.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return hoursSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });
    return entries;
  };

  // Load community service tracker when case data is available
  useEffect(() => {
    if (caseData?.id && isCommunityServiceCase) {
      fetchCommunityServiceTracker();
    }
  }, [caseData?.id, isCommunityServiceCase]);

  // Fetch Quick Note templates for Food Pantry cases
  useEffect(() => {
    if (isFoodPantryCase && caseData?.status === 'open') {
      fetchQuickNoteTemplates();
    }
  }, [isFoodPantryCase, caseData?.status]);

  // Fetch Quick Note templates for MTAJ-NC cases
  useEffect(() => {
    if (isMtajNcCase && caseData?.status === 'open') {
      fetchMtajQuickNoteTemplates();
    }
  }, [isMtajNcCase, caseData?.status]);

  // Fetch Quick Note templates for SNAP Outreach NC cases
  useEffect(() => {
    if (isSnapOutreachNcCase && caseData?.status === 'open') {
      fetchSnapOutreachTemplates();
    }
  }, [isSnapOutreachNcCase, caseData?.status]);

  // Fetch Settings-configured quick notes for this case's type
  useEffect(() => {
    if (caseData?.id && caseData?.status === 'open') {
      axios.get(`${API}/cases/${caseData.id}/custom-quick-notes`)
        .then(res => setCustomQuickNotes(res.data || []))
        .catch(() => setCustomQuickNotes([]));
    }
  }, [caseData?.id, caseData?.status]);

  // Fetch Quick Note templates for Addictions Recovery Program cases
  useEffect(() => {
    if (isAddictionsRecoveryCase && caseData?.status === 'open') {
      fetchArpQuickNoteTemplates();
    }
  }, [isAddictionsRecoveryCase, caseData?.status]);

  const fetchQuickNoteTemplates = async () => {
    try {
      const response = await axios.get(`${API}/settings/food-pantry-quick-note-templates`);
      setQuickNoteTemplates(response.data);
    } catch (error) {
      console.error('Failed to load quick note templates:', error);
    }
  };

  const fetchMtajQuickNoteTemplates = async () => {
    try {
      const response = await axios.get(`${API}/settings/mtaj-nc-quick-note-templates`);
      setMtajQuickNoteTemplates(response.data);
    } catch (error) {
      console.error('Failed to load MTAJ-NC quick note templates:', error);
    }
  };

  const fetchSnapOutreachTemplates = async () => {
    try {
      const response = await axios.get(`${API}/settings/snap-outreach-quick-note-templates`);
      setSnapOutreachTemplates(response.data);
    } catch (error) {
      console.error('Failed to load SNAP Outreach quick note templates:', error);
    }
  };

  const handleCreateQuickNote = async (templateId) => {
    setCreatingQuickNote(true);
    try {
      const response = await axios.post(`${API}/cases/${id}/quick-note?template_id=${templateId}`);
      toast.success('Quick Note created successfully');
      // Refresh notes
      const notesRes = await axios.get(`${API}/case-notes?case_id=${id}`);
      setNotes(notesRes.data.items);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Quick Note');
    } finally {
      setCreatingQuickNote(false);
    }
  };

  const handleMtajQuickNoteClick = (template) => {
    if (template.requires_outcome) {
      // Show outcome dialog for Reverse Referral
      setMtajReverseReferralDialogOpen(true);
      setMtajOutcome('');
    } else {
      // Direct Referral - create immediately
      handleCreateMtajQuickNote(template.id, null);
    }
  };

  // Ask the enrollment summary questions first when a quick note's templates need them
  const runWithEnrollmentSummary = (needsSummary, run) => {
    if (needsSummary) {
      setPendingSummaryRun(() => run);
    } else {
      run(null);
    }
  };

  const handleCreateMtajQuickNote = (templateId, outcome) => {
    const tpl = mtajQuickNoteTemplates.find(t => t.id === templateId);
    runWithEnrollmentSummary(tpl?.needs_enrollment_summary, (summary) => doCreateMtajQuickNote(templateId, outcome, summary));
  };

  const doCreateMtajQuickNote = async (templateId, outcome, enrollmentSummary) => {
    setCreatingMtajQuickNote(true);
    try {
      const response = await axios.post(`${API}/cases/${id}/mtaj-quick-note`, {
        template_id: templateId,
        outcome: outcome,
        enrollment_summary: enrollmentSummary
      });
      toast.success('MTAJ-NC Quick Note created successfully');
      setMtajReverseReferralDialogOpen(false);
      setMtajOutcome('');
      // Refresh notes
      const notesRes = await axios.get(`${API}/case-notes?case_id=${id}`);
      setNotes(notesRes.data.items);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Quick Note');
    } finally {
      setCreatingMtajQuickNote(false);
    }
  };

  const handleCreateSnapOutreachNote = async (templateId) => {
    setCreatingSnapOutreachNote(true);
    try {
      const response = await axios.post(`${API}/cases/${id}/snap-outreach-quick-note?template_id=${templateId}`);
      toast.success('SNAP Outreach Quick Note created successfully');
      // Refresh notes
      const notesRes = await axios.get(`${API}/case-notes?case_id=${id}`);
      setNotes(notesRes.data.items);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create SNAP Outreach Quick Note');
    } finally {
      setCreatingSnapOutreachNote(false);
    }
  };

  const fetchArpQuickNoteTemplates = async () => {
    try {
      const response = await axios.get(`${API}/settings/arp-quick-note-templates`);
      setArpQuickNoteTemplates(response.data);
    } catch (error) {
      console.error('Failed to load ARP quick note templates:', error);
    }
  };

  const handleCreateArpQuickNote = (templateId) => {
    const tpl = arpQuickNoteTemplates.find(t => t.id === templateId);
    runWithEnrollmentSummary(tpl?.needs_enrollment_summary, (summary) => doCreateArpQuickNote(templateId, summary));
  };

  const doCreateArpQuickNote = async (templateId, enrollmentSummary) => {
    setCreatingArpQuickNote(true);
    try {
      const response = await axios.post(`${API}/cases/${id}/arp-quick-note?template_id=${templateId}`, enrollmentSummary ? { enrollment_summary: enrollmentSummary } : undefined);
      const arpCount = response.data.notes?.length || 4;
      toast.success(`Successfully created ${arpCount} ARP case note${arpCount === 1 ? '' : 's'}`);
      // Refresh notes
      const notesRes = await axios.get(`${API}/case-notes?case_id=${id}`);
      setNotes(notesRes.data.items);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ARP Quick Notes');
    } finally {
      setCreatingArpQuickNote(false);
    }
  };

  const handleCreateCustomQuickNote = (item) => {
    runWithEnrollmentSummary(item.needs_enrollment_summary, (summary) => doCreateCustomQuickNote(item, summary));
  };

  const doCreateCustomQuickNote = async (item, enrollmentSummary) => {
    setCreatingCustomQuickNote(true);
    try {
      const payload = item.is_run_all ? { group_id: item.group_id, run_all: true } : { group_id: item.group_id, note_id: item.note_id };
      if (enrollmentSummary) payload.enrollment_summary = enrollmentSummary;
      const response = await axios.post(`${API}/cases/${id}/custom-quick-note`, payload);
      const qnCount = response.data.notes?.length || 0;
      toast.success(`Quick Note created: ${qnCount} case note${qnCount === 1 ? '' : 's'} added`);
      const notesRes = await axios.get(`${API}/case-notes?case_id=${id}`);
      setNotes(notesRes.data.items);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Quick Note');
    } finally {
      setCreatingCustomQuickNote(false);
    }
  };

  const runAllQuickNotes = customQuickNotes.filter(item => item.is_run_all);
  const individualQuickNotes = customQuickNotes.filter(item => !item.is_run_all);

  const renderRunAllItems = (withSeparator = true) => runAllQuickNotes.length > 0 && (
    <>
      {runAllQuickNotes.map((item) => (
        <DropdownMenuItem
          key={item.note_id}
          onClick={() => handleCreateCustomQuickNote(item)}
          disabled={creatingCustomQuickNote}
          className="cursor-pointer"
          data-testid={`run-all-quick-notes-${item.group_id}`}
        >
          <Zap className="w-4 h-4 mr-2 text-amber-500" />
          <div className="flex flex-col">
            <span className="font-medium">{item.name}</span>
            <span className="text-xs text-muted-foreground">Runs {item.note_count} quick note{item.note_count === 1 ? '' : 's'} ({item.template_count} case note{item.template_count === 1 ? '' : 's'})</span>
          </div>
        </DropdownMenuItem>
      ))}
      {withSeparator && <DropdownMenuSeparator />}
    </>
  );

  const renderCustomQuickNoteItems = (withSeparator = true) => individualQuickNotes.length > 0 && (
    <>
      {withSeparator && <DropdownMenuSeparator />}
      {individualQuickNotes.map((item) => (
        <DropdownMenuItem
          key={item.note_id}
          onClick={() => handleCreateCustomQuickNote(item)}
          disabled={creatingCustomQuickNote}
          className="cursor-pointer"
          data-testid={`custom-quick-note-option-${item.note_id}`}
        >
          <FileText className="w-4 h-4 mr-2 text-muted-foreground" />
          <div className="flex flex-col">
            <span className="font-medium">{item.name}</span>
            <span className="text-xs text-muted-foreground">Creates {item.template_count} case note{item.template_count === 1 ? '' : 's'}</span>
          </div>
        </DropdownMenuItem>
      ))}
    </>
  );

  const handleCloseCase = async () => {
    setClosing(true);
    try {
      // Build query params including exit_outcome for SNAP E&T cases
      let queryParams = `reason=${encodeURIComponent(closeReason)}&close_enrollment=true`;
      if (closeExitOutcome && isSnapEtCase) {
        queryParams += `&exit_outcome=${encodeURIComponent(closeExitOutcome)}`;
      }
      
      const response = await axios.post(`${API}/cases/${id}/close?${queryParams}`);
      if (response.data.enrollment_closed) {
        toast.success('Case and associated enrollment closed successfully');
      } else {
        toast.success('Case closed successfully');
      }
      setCloseDialogOpen(false);
      setCloseReason('');
      setCloseExitOutcome('');
      fetchCaseData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close case');
    } finally {
      setClosing(false);
    }
  };

  const fetchStaffList = async () => {
    try {
      const response = await axios.get(`${API}/users/staff`);
      setStaffList(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch staff list:', error);
    }
  };

  const handleAssignCase = async () => {
    setAssigning(true);
    try {
      // Handle "unassigned" case - pass null to clear assignment
      const assignedStaffId = selectedStaff === 'unassigned' ? null : selectedStaff;
      await axios.put(`${API}/cases/${id}`, { assigned_staff_id: assignedStaffId });
      toast.success(selectedStaff === 'unassigned' ? 'Case unassigned successfully' : 'Case assigned successfully');
      setShowAssignDialog(false);
      setSelectedStaff('');
      fetchCaseData();
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign case');
    } finally {
      setAssigning(false);
    }
  };

  const openDeleteNoteDialog = (note) => {
    setNoteToDelete(note);
    setDeleteReason('');
    setDeleteNoteDialogOpen(true);
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete || !deleteReason.trim()) {
      toast.error('Please provide a reason for deletion');
      return;
    }
    
    setDeletingNote(true);
    try {
      await axios.delete(`${API}/case-notes/${noteToDelete.id}`, {
        data: { reason: deleteReason.trim() }
      });
      toast.success('Note deleted successfully');
      setDeleteNoteDialogOpen(false);
      setNoteToDelete(null);
      setDeleteReason('');
      fetchCaseData(); // Refresh notes
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete note');
    } finally {
      setDeletingNote(false);
    }
  };

  // Request review on a case note
  const openReviewDialog = (note) => {
    setNoteToReview(note);
    setReviewComment('');
    setReviewDialogOpen(true);
  };

  const handleRequestReview = async () => {
    if (!noteToReview || !reviewComment.trim()) {
      toast.error('Please provide a comment explaining what needs to be corrected');
      return;
    }
    
    setRequestingReview(true);
    try {
      await axios.post(`${API}/case-notes/${noteToReview.id}/request-review`, {
        comment: reviewComment.trim()
      });
      toast.success('Review requested. The author has been notified.');
      setReviewDialogOpen(false);
      setNoteToReview(null);
      setReviewComment('');
      fetchCaseData(); // Refresh notes
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request review');
    } finally {
      setRequestingReview(false);
    }
  };

  // Submit correction for a case note
  const openCorrectionDialog = (note) => {
    setNoteToCorrected(note);
    setCorrectionNotes('');
    setCorrectionDialogOpen(true);
  };

  const handleSubmitCorrection = async () => {
    if (!noteToCorrected) return;
    
    setSubmittingCorrection(true);
    try {
      await axios.post(`${API}/case-notes/${noteToCorrected.id}/submit-correction`, {
        correction_notes: correctionNotes.trim() || null
      });
      toast.success('Correction submitted. The reviewer has been notified.');
      setCorrectionDialogOpen(false);
      setNoteToCorrected(null);
      setCorrectionNotes('');
      fetchCaseData(); // Refresh notes
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit correction');
    } finally {
      setSubmittingCorrection(false);
    }
  };

  // Approve correction (for reviewers)
  const handleApproveCorrection = async (noteId) => {
    try {
      await axios.post(`${API}/case-notes/${noteId}/approve-correction`);
      toast.success('Correction approved successfully');
      fetchCaseData(); // Refresh notes
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve correction');
    }
  };

  // Toggle highlight on a case note
  const handleToggleHighlight = async (note, shouldHighlight) => {
    setHighlightLoading(note.id);
    try {
      const response = await axios.post(`${API}/case-notes/${note.id}/toggle-highlight`, {
        note_id: note.id,
        highlight: shouldHighlight
      });
      toast.success(response.data.message);
      fetchCaseData(); // Refresh notes
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update highlight status');
    } finally {
      setHighlightLoading(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'paused': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'closed': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!caseData) return null;

  // Get display name for case (service type or case type)
  const caseDisplayName = caseData.service_type_name || caseData.service_type?.replace(/_/g, ' ') || caseData.case_type?.replace(/_/g, ' ') || 'Case';

  // Handle back navigation - preserve participant context if available
  const handleBackNavigation = () => {
    if (caseData.participant_id) {
      // Navigate back to participant detail page with cases tab active
      navigate(`/participants/${caseData.participant_id}`, { 
        state: { activeTab: 'cases' }
      });
    } else {
      // Fallback to cases list
      navigate('/cases');
    }
  };

  return (
    <div className="space-y-6" data-testid="case-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={handleBackNavigation} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold tracking-tight capitalize">
                {caseDisplayName}
              </h1>
              <Badge className={getStatusColor(caseData.status)}>
                {caseData.status}
              </Badge>
              <Badge className={getPriorityColor(caseData.priority)}>
                {caseData.priority}
              </Badge>
            </div>
            <p className="text-muted-foreground mt-1 font-mono text-sm">{caseData.id}</p>
            {/* Program/State/Contract info */}
            {caseData.program_name && (
              <div className="flex flex-wrap gap-3 mt-2 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Briefcase className="w-4 h-4" />
                  {caseData.program_name}
                </span>
                {caseData.state && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {caseData.state}
                  </span>
                )}
                {caseData.contract_name && (
                  <span className="flex items-center gap-1">
                    <Building className="w-4 h-4" />
                    {caseData.contract_name}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {caseData.status !== 'closed' && (
            <>
              <Button asChild data-testid="add-note-btn">
                <Link to={`/case-notes/new?case=${id}`}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Note
                </Link>
              </Button>
              <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="destructive" data-testid="close-case-btn">
                    <XCircle className="w-4 h-4 mr-2" />
                    Close Case
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Close Case</DialogTitle>
                    <DialogDescription>
                      Closing this case will also close the associated enrollment. Do you want to proceed?
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-amber-800">
                        <p className="font-medium">This action will:</p>
                        <ul className="list-disc ml-4 mt-1 space-y-1">
                          <li>Close this case</li>
                          <li>Close the associated program enrollment</li>
                          <li>Sync the closure date for both records</li>
                          {isSnapEtCase && <li>Update Exit Outcome on Targeted Outcomes and Caseload Review</li>}
                        </ul>
                      </div>
                    </div>
                    
                    {/* Exit Outcome dropdown for SNAP E&T cases */}
                    {isSnapEtCase && (
                      <div className="space-y-2">
                        <Label htmlFor="exit-outcome" className="flex items-center gap-2">
                          Exit Outcome
                          <span className="text-xs text-muted-foreground">(Required for SNAP E&T)</span>
                        </Label>
                        <Select value={closeExitOutcome} onValueChange={setCloseExitOutcome}>
                          <SelectTrigger id="exit-outcome" data-testid="close-exit-outcome-select">
                            <SelectValue placeholder="Select exit outcome" />
                          </SelectTrigger>
                          <SelectContent>
                            {EXIT_OUTCOME_OPTIONS.map((outcome) => (
                              <SelectItem key={outcome} value={outcome}>{outcome}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          This will sync to the Targeted Outcomes card and Caseload Review
                        </p>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label htmlFor="close-reason">Closure Reason</Label>
                      <Textarea
                        id="close-reason"
                        value={closeReason}
                        onChange={(e) => setCloseReason(e.target.value)}
                        placeholder="Enter the reason for closing this case..."
                        rows={3}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCloseDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      variant="destructive" 
                      onClick={handleCloseCase} 
                      disabled={closing || (isSnapEtCase && !closeExitOutcome)}
                    >
                      {closing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Close Case & Enrollment
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Quick Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Participant</p>
                <Link 
                  to={`/participants/${caseData.participant_id}`} 
                  className="font-medium text-primary hover:underline"
                  data-testid="participant-link"
                >
                  {caseData.participant_name}
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Start Date</p>
                <p className="font-medium">{formatDateOnly(caseData.start_date)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Hide Assigned To card for Food Pantry and Addictions Recovery programs */}
        {!UNASSIGNED_PROGRAMS.some(program => caseData.program_name?.toLowerCase().includes(program)) && (
          <Card className="card-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Assigned To</p>
                    <p className="font-medium">{caseData.assigned_staff_name || 'Unassigned'}</p>
                  </div>
                </div>
                {canAssignCases && caseData.status !== 'closed' && (
                  <Dialog open={showAssignDialog} onOpenChange={(open) => {
                    setShowAssignDialog(open);
                    if (open) {
                      fetchStaffList();
                      setSelectedStaff(caseData.assigned_staff_id || '');
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="assign-case-btn">
                        <UserCog className="w-4 h-4 mr-1" />
                        Assign
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Assign Case</DialogTitle>
                        <DialogDescription>
                          Select a staff member to assign this case to.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Assign To</Label>
                          <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                            <SelectTrigger data-testid="assign-staff-select">
                              <SelectValue placeholder="Select staff member" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">
                                <span className="text-muted-foreground">Unassigned</span>
                              </SelectItem>
                              {staffList.map(staff => (
                                <SelectItem key={staff.id} value={staff.id}>
                                  {staff.name} ({staff.role?.replace(/_/g, ' ')})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
                        <Button onClick={handleAssignCase} disabled={assigning}>
                          {assigning && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          Assign
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="card-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Funding Source</p>
                <p className="font-medium">{caseData.funding_source || 'Not specified'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue={defaultTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="communications" data-testid="tab-communications">Communications</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes ({notes.length})</TabsTrigger>
          <TabsTrigger value="appointments" data-testid="tab-appointments">
            <CalendarDays className="w-4 h-4 mr-1" />
            Appointments ({appointments.length})
          </TabsTrigger>
          {isSnapEtCase && (
            <TabsTrigger value="costs" data-testid="tab-participant-costs">
              <DollarSign className="w-4 h-4 mr-1" />
              Participant Costs
            </TabsTrigger>
          )}
          {isSnapEtCase && (
            <TabsTrigger value="ssr-tickets" data-testid="tab-ssr-tickets">
              Supportive Service Request Tickets
            </TabsTrigger>
          )}
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Target Outcomes - For SNAP E&T cases ONLY (not Food Pantry, Addictions, or Community Service) */}
            {isSnapEtCase && (
              <SnapEtTargetedOutcomes 
                caseId={id} 
                caseData={caseData} 
                onUpdate={fetchCaseData}
                employmentOutcomeComplete={employmentOutcomeComplete}
                onRetentionValidationFailed={() => {
                  // When retention validation fails, set pending flag and trigger employment outcome edit
                  setPendingRetentionEnable(true);
                  if (employmentOutcomeRef?.openEditDialog) {
                    employmentOutcomeRef.openEditDialog();
                  }
                }}
                pendingRetentionEnable={pendingRetentionEnable}
                onRetentionEnabled={() => setPendingRetentionEnable(false)}
                userRole={user?.role}
              />
            )}

            {/* Employment Outcome - For SNAP E&T cases only, positioned next to Targeted Outcomes */}
            <EmploymentOutcomeCard 
              caseId={id} 
              caseData={caseData}
              onUpdate={(isComplete) => {
                fetchCaseData();
                setEmploymentOutcomeComplete(isComplete);
                // If we were waiting to enable retention, notify the component
                if (pendingRetentionEnable && isComplete) {
                  // The SnapEtTargetedOutcomes will handle enabling retention
                }
              }}
              onRef={(ref) => setEmploymentOutcomeRef(ref)}
              onCompletionStatusChange={(isComplete) => setEmploymentOutcomeComplete(isComplete)}
            />

            {/* SNAP E&T Program Report Card - For SNAP E&T cases only, under Targeted Outcomes */}
            <SnapEtProgramReportCard 
              caseId={id} 
              caseData={caseData}
              onUpdate={fetchCaseData}
            />

            {/* Household Details - For Food Pantry cases only */}
            <HouseholdDetailsCard 
              caseId={id} 
              caseData={caseData}
              participant={participant}
              onUpdate={fetchCaseData}
            />

            {/* Screening Instrument - For Addictions Recovery cases only */}
            <AddictionsScreeningInstrumentCard 
              caseId={id} 
              caseData={caseData}
              onUpdate={fetchCaseData}
            />

            {/* Community Service Tracker - For Community Service cases only */}
            {isCommunityServiceCase && (
              <Card className="card-shadow" data-testid="community-service-tracker-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Community Service Tracker
                  </CardTitle>
                  <CardDescription>
                    Track community service hours and requirements
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {loadingCommunityService ? (
                    <div className="flex items-center justify-center py-8" data-testid="community-service-loading">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : (
                    <>
                      {/* Settings Section */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="community-service-settings">
                        <div className="space-y-2">
                          <Label>Community Service Type</Label>
                          <Select
                            value={communityServiceForm.service_type}
                            onValueChange={(v) => setCommunityServiceForm(prev => ({ ...prev, service_type: v }))}
                          >
                            <SelectTrigger data-testid="service-type-select">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Court-Ordered">Court-Ordered</SelectItem>
                              <SelectItem value="ABAWD Compliance">ABAWD Compliance</SelectItem>
                              <SelectItem value="Civic Volunteer">Civic Volunteer</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {communityServiceForm.service_type === 'Other' && (
                          <div className="space-y-2">
                            <Label>Specify Type</Label>
                            <Input
                              data-testid="service-type-other-input"
                              value={communityServiceForm.service_type_other}
                              onChange={(e) => setCommunityServiceForm(prev => ({ ...prev, service_type_other: e.target.value }))}
                              placeholder="Enter service type"
                            />
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label>Verification Required</Label>
                          <Select
                            value={communityServiceForm.verification_required === null ? '' : String(communityServiceForm.verification_required)}
                            onValueChange={(v) => setCommunityServiceForm(prev => ({ ...prev, verification_required: v === 'true' }))}
                          >
                            <SelectTrigger data-testid="verification-required-select">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="true">Yes</SelectItem>
                              <SelectItem value="false">No</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {communityServiceForm.service_type === 'Court-Ordered' && (
                          <div className="space-y-2">
                            <Label>Court-Order on File</Label>
                            <Select
                              value={communityServiceForm.court_order_on_file === null ? '' : String(communityServiceForm.court_order_on_file)}
                              onValueChange={(v) => setCommunityServiceForm(prev => ({ ...prev, court_order_on_file: v === 'true' }))}
                            >
                              <SelectTrigger data-testid="court-order-select">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="true">Yes</SelectItem>
                                <SelectItem value="false">No</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label>Required Hours</Label>
                          <Input
                            data-testid="required-hours-input"
                            type="number"
                            min="0"
                            value={communityServiceForm.required_hours}
                            onChange={(e) => setCommunityServiceForm(prev => ({ ...prev, required_hours: parseFloat(e.target.value) || 0 }))}
                            placeholder="Enter required hours"
                          />
                        </div>
                      </div>

                      <Button onClick={saveCommunityServiceTracker} disabled={savingCommunityService} size="sm" data-testid="save-settings-btn">
                        {savingCommunityService ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Save Settings
                      </Button>

                      {/* Hours Logging Section */}
                      <div className="border-t pt-4 space-y-4" data-testid="hours-logging-section">
                        <h4 className="font-medium">Log Hours</h4>
                        <div className="flex flex-wrap items-end gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Date</Label>
                            <Input
                              data-testid="log-hours-date-input"
                              type="date"
                              value={hoursLogForm.date}
                              onChange={(e) => setHoursLogForm(prev => ({ ...prev, date: e.target.value }))}
                              className="w-40"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Hours</Label>
                            <Input
                              data-testid="log-hours-input"
                              type="number"
                              min="0"
                              step="0.5"
                              value={hoursLogForm.hours}
                              onChange={(e) => setHoursLogForm(prev => ({ ...prev, hours: e.target.value }))}
                              placeholder="0"
                              className="w-24"
                            />
                          </div>
                          <Button onClick={logCommunityServiceHours} disabled={loggingHours} size="sm" data-testid="log-hours-btn">
                            {loggingHours ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                            Log Hours
                          </Button>
                        </div>
                      </div>

                      {/* Hours Table */}
                      {communityServiceTracker?.hours_entries?.length > 0 && (
                        <div className="border rounded-lg overflow-hidden" data-testid="hours-table">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-muted/50">
                                <TableHead 
                                  className="cursor-pointer hover:bg-muted transition-colors"
                                  onClick={() => setHoursSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                                  data-testid="hours-table-date-header"
                                >
                                  <div className="flex items-center gap-1">
                                    Date
                                    {hoursSortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                                  </div>
                                </TableHead>
                                <TableHead>Hours Completed</TableHead>
                                <TableHead className="w-16"></TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {getSortedHoursEntries().map((entry) => (
                                <TableRow key={entry.id} data-testid={`hours-entry-${entry.id}`}>
                                  <TableCell>{formatDateUtil(entry.date)}</TableCell>
                                  <TableCell data-testid={`hours-value-${entry.id}`}>{entry.hours}</TableCell>
                                  <TableCell>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => deleteCommunityServiceHours(entry.id)}
                                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                                      data-testid={`delete-hours-${entry.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}

                      {/* Totals */}
                      <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg" data-testid="hours-totals">
                        <div>
                          <p className="text-sm text-muted-foreground">Total Hours Completed</p>
                          <p className="text-2xl font-bold text-green-600" data-testid="total-hours-completed">
                            {communityServiceTracker?.total_hours_completed || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Total Hours Remaining</p>
                          <p className="text-2xl font-bold text-orange-600" data-testid="total-hours-remaining">
                            {communityServiceTracker?.total_hours_remaining || communityServiceForm.required_hours || 0}
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="documents">
          <CaseDocumentsPanel 
            caseId={id} 
            caseName={caseDisplayName}
            participantId={caseData?.participant_id}
            onDocumentAdded={fetchCaseData}
          />
        </TabsContent>

        <TabsContent value="communications">
          {participant && (
            <CommunicationsPanel
              participantId={caseData.participant_id}
              participantName={caseData.participant_name}
              participantPhone={participant.phone}
              participantEmail={participant.email}
              caseId={caseData.id}
              onCommunicationSent={fetchCaseData}
            />
          )}
          {!participant && (
            <Card className="card-shadow">
              <CardContent className="py-8 text-center text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Loading participant information...
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="notes">
          <Card className="card-shadow">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Case Notes</CardTitle>
                <CardDescription>
                  All notes for this case. Click Edit to modify or Delete to remove.
                </CardDescription>
              </div>
              <div className="flex gap-2">
                {/* Quick Note Button - For Food Pantry cases */}
                {isFoodPantryCase && caseData?.status === 'open' && quickNoteTemplates.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={creatingQuickNote}
                        data-testid="quick-note-btn"
                      >
                        {creatingQuickNote ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Zap className="w-4 h-4 mr-2" />
                        )}
                        Quick Note
                        <ChevronDown className="w-4 h-4 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-80">
                      {renderRunAllItems()}
                      {quickNoteTemplates.map((template) => (
                        <DropdownMenuItem
                          key={template.id}
                          onClick={() => handleCreateQuickNote(template.id)}
                          className="cursor-pointer"
                          data-testid={`quick-note-option-${template.id}`}
                        >
                          <FileText className="w-4 h-4 mr-2 text-muted-foreground" />
                          <span className="truncate">{template.subject}</span>
                        </DropdownMenuItem>
                      ))}
                      {renderCustomQuickNoteItems()}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                
                {/* Quick Note Button - For MTAJ-NC Referral cases */}
                {isMtajNcCase && caseData?.status === 'open' && mtajQuickNoteTemplates.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={creatingMtajQuickNote}
                        data-testid="mtaj-quick-note-btn"
                      >
                        {creatingMtajQuickNote ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Zap className="w-4 h-4 mr-2" />
                        )}
                        Quick Note
                        <ChevronDown className="w-4 h-4 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                      {renderRunAllItems()}
                      {mtajQuickNoteTemplates.map((template) => (
                        <DropdownMenuItem
                          key={template.id}
                          onClick={() => handleMtajQuickNoteClick(template)}
                          className="cursor-pointer"
                          data-testid={`mtaj-quick-note-option-${template.id}`}
                        >
                          <FileText className="w-4 h-4 mr-2 text-muted-foreground" />
                          <span>{template.name}</span>
                        </DropdownMenuItem>
                      ))}
                      {renderCustomQuickNoteItems()}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Quick Note Button - For SNAP Outreach NC cases */}
                {isSnapOutreachNcCase && caseData?.status === 'open' && snapOutreachTemplates.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={creatingSnapOutreachNote}
                        data-testid="snap-outreach-quick-note-btn"
                      >
                        {creatingSnapOutreachNote ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Zap className="w-4 h-4 mr-2" />
                        )}
                        Quick Note
                        <ChevronDown className="w-4 h-4 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72">
                      {renderRunAllItems()}
                      {snapOutreachTemplates.map((template) => (
                        <DropdownMenuItem
                          key={template.id}
                          onClick={() => handleCreateSnapOutreachNote(template.id)}
                          className="cursor-pointer"
                          data-testid={`snap-outreach-quick-note-option-${template.id}`}
                        >
                          <FileText className="w-4 h-4 mr-2 text-muted-foreground" />
                          <span className="truncate">{template.subject}</span>
                        </DropdownMenuItem>
                      ))}
                      {renderCustomQuickNoteItems()}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                
                {/* Quick Note Button - Settings-configured quick notes for other case types */}
                {caseData?.status === 'open' && customQuickNotes.length > 0 && !(
                  (isFoodPantryCase && quickNoteTemplates.length > 0) ||
                  (isMtajNcCase && mtajQuickNoteTemplates.length > 0) ||
                  (isSnapOutreachNcCase && snapOutreachTemplates.length > 0) ||
                  (isAddictionsRecoveryCase && arpQuickNoteTemplates.length > 0)
                ) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" disabled={creatingCustomQuickNote} data-testid="custom-quick-note-btn">
                        {creatingCustomQuickNote ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                        Quick Note
                        <ChevronDown className="w-4 h-4 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72">
                      {renderRunAllItems(individualQuickNotes.length > 0)}
                      {renderCustomQuickNoteItems(false)}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Quick Note Button - For Addictions Recovery Program cases */}
                {isAddictionsRecoveryCase && caseData?.status === 'open' && arpQuickNoteTemplates.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={creatingArpQuickNote}
                        data-testid="arp-quick-note-btn"
                      >
                        {creatingArpQuickNote ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Zap className="w-4 h-4 mr-2" />
                        )}
                        Quick Note
                        <ChevronDown className="w-4 h-4 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72">
                      {renderRunAllItems()}
                      {arpQuickNoteTemplates.map((template) => (
                        <DropdownMenuItem
                          key={template.id}
                          onClick={() => handleCreateArpQuickNote(template.id)}
                          className="cursor-pointer"
                          data-testid={`arp-quick-note-option-${template.id}`}
                        >
                          <FileText className="w-4 h-4 mr-2 text-muted-foreground" />
                          <div className="flex flex-col">
                            <span className="font-medium">{template.name}</span>
                            <span className="text-xs text-muted-foreground">{template.description}</span>
                          </div>
                        </DropdownMenuItem>
                      ))}
                      {renderCustomQuickNoteItems()}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                
                <Button size="sm" asChild>
                  <Link to={`/case-notes/new?case=${id}`}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Note
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {notes.length > 0 ? (
                <div className="space-y-4">
                  {notes.map((note) => {
                    // Determine left border color based on note creation method
                    const isDocumentNote = note.is_system_generated && note.note_type === 'document';
                    const isCommunicationNote = note.is_system_generated && note.note_type === 'communication';
                    const isAppointmentNote = note.is_system_generated && note.note_type === 'appointment';
                    const isCommunityServiceNote = note.is_system_generated && note.note_type === 'community_service';
                    
                    let leftBorderClass = '';
                    if (isDocumentNote) {
                      leftBorderClass = 'border-l-4 border-l-green-500';
                    } else if (isCommunicationNote) {
                      leftBorderClass = 'border-l-4 border-l-blue-500';
                    } else if (isAppointmentNote || isCommunityServiceNote) {
                      leftBorderClass = 'border-l-4 border-l-pink-500';
                    }
                    
                    return (
                    <div 
                      key={note.id} 
                      className={`p-4 rounded-lg border transition-colors ${leftBorderClass} ${
                        note.is_highlighted
                          ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border-2'
                          : note.review_status === 'needs_review' && note.author_id === user?.id
                          ? 'border-orange-400 bg-orange-50/50 dark:bg-orange-900/10'
                          : note.review_status === 'corrected' && canRequestReview
                          ? 'border-blue-400 bg-blue-50/50 dark:bg-blue-900/10'
                          : 'border-border hover:bg-muted/50'
                      }`}
                    >
                      {/* Highlighted/Pinned Alert Banner */}
                      {note.is_highlighted && (
                        <div className="flex items-center gap-2 p-3 mb-3 rounded-lg bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                          <Pin className="w-5 h-5 shrink-0" />
                          <div className="flex-1">
                            <p className="font-medium">Pinned Alert (Internal)</p>
                            <p className="text-xs">Highlighted by {note.highlighted_by_name} on {formatDateUtil(note.highlighted_at)}</p>
                          </div>
                          {canHighlightNotes && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="shrink-0 border-yellow-400 text-yellow-700 hover:bg-yellow-200"
                              onClick={() => handleToggleHighlight(note, false)}
                              disabled={highlightLoading === note.id}
                            >
                              {highlightLoading === note.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <PinOff className="w-4 h-4 mr-1" />
                                  Unpin
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      )}
                      
                      {/* Internal Badge for System-Generated Notes */}
                      {isDocumentNote && (
                        <div className="flex items-center gap-2 p-2 mb-3 rounded-lg bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                          <FileText className="w-4 h-4 shrink-0" />
                          <p className="font-medium text-sm">Internal</p>
                          <span className="text-xs">Auto-generated from document upload</span>
                        </div>
                      )}
                      
                      {isCommunicationNote && (
                        <div className="flex items-center gap-2 p-2 mb-3 rounded-lg bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                          <MessageSquare className="w-4 h-4 shrink-0" />
                          <p className="font-medium text-sm">Internal</p>
                          <span className="text-xs">Auto-generated from communication</span>
                        </div>
                      )}
                      
                      {isAppointmentNote && (
                        <div className="flex items-center gap-2 p-2 mb-3 rounded-lg bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300">
                          <CalendarDays className="w-4 h-4 shrink-0" />
                          <p className="font-medium text-sm">Internal</p>
                          <span className="text-xs">Auto-generated from appointment action</span>
                        </div>
                      )}
                      
                      {isCommunityServiceNote && (
                        <div className="flex items-center gap-2 p-2 mb-3 rounded-lg bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300">
                          <Clock className="w-4 h-4 shrink-0" />
                          <p className="font-medium text-sm">Internal</p>
                          <span className="text-xs">Auto-generated from community service tracker</span>
                        </div>
                      )}
                      
                      {/* Review Alert Banner */}
                      {note.review_status === 'needs_review' && note.author_id === user?.id && (
                        <div className="flex items-center gap-2 p-3 mb-3 rounded-lg bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                          <AlertCircle className="w-5 h-5 shrink-0" />
                          <div className="flex-1">
                            <p className="font-medium">Review Required</p>
                            <p className="text-sm">{note.current_review?.comment}</p>
                            <p className="text-xs mt-1">Requested by {note.current_review?.reviewer_name} on {formatDateUtil(note.current_review?.requested_at)}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="shrink-0 border-orange-400 text-orange-700 hover:bg-orange-200"
                            onClick={() => navigate(`/case-notes/${note.id}/edit`)}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit & Fix
                          </Button>
                          <Button
                            size="sm"
                            className="shrink-0 bg-orange-600 hover:bg-orange-700"
                            onClick={() => openCorrectionDialog(note)}
                          >
                            <CheckCheck className="w-4 h-4 mr-1" />
                            Submit Correction
                          </Button>
                        </div>
                      )}
                      
                      {/* Correction Pending Approval Banner (for reviewers) */}
                      {note.review_status === 'corrected' && canRequestReview && note.current_review?.reviewer_id === user?.id && (
                        <div className="flex items-center gap-2 p-3 mb-3 rounded-lg bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                          <MessageCircle className="w-5 h-5 shrink-0" />
                          <div className="flex-1">
                            <p className="font-medium">Correction Submitted - Awaiting Your Approval</p>
                            {note.current_review?.correction_notes && (
                              <p className="text-sm">Author notes: {note.current_review.correction_notes}</p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            className="shrink-0 bg-blue-600 hover:bg-blue-700"
                            onClick={() => handleApproveCorrection(note.id)}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                        </div>
                      )}

                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          {/* Subject Line */}
                          <h4 className="font-semibold text-base mb-2">
                            {note.subject || 'Untitled Note'}
                          </h4>
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge variant="outline" className="capitalize">{note.note_type?.replace(/_/g, ' ')}</Badge>
                            <Badge className={`status-${note.status}`}>{note.status}</Badge>
                            {/* Highlighted/Pinned Badge */}
                            {note.is_highlighted && (
                              <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 border border-yellow-300">
                                <Pin className="w-3 h-3 mr-1" />
                                Pinned Alert
                              </Badge>
                            )}
                            {/* Internal Badge (shown when highlighted) */}
                            {note.is_internal && (
                              <Badge variant="outline" className="text-xs border-gray-400 text-gray-600">
                                Internal
                              </Badge>
                            )}
                            {/* Review Status Badge */}
                            {note.review_status === 'needs_review' && (
                              <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Needs Review
                              </Badge>
                            )}
                            {note.review_status === 'corrected' && (
                              <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                                <MessageCircle className="w-3 h-3 mr-1" />
                                Corrected
                              </Badge>
                            )}
                            {note.review_status === 'approved' && (
                              <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Approved
                              </Badge>
                            )}
                            {note.is_quick_note && (
                              <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800">
                                <Zap className="w-3 h-3 mr-1" />
                                Quick Note
                              </Badge>
                            )}
                            {note.is_system_generated && (
                              <Badge variant="secondary" className="text-xs">System Generated</Badge>
                            )}
                            <span className="text-sm text-muted-foreground flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {(() => {
                                const dateStr = note.date_of_service || note.created_at;
                                if (!dateStr) return '-';
                                return formatDateUtil(dateStr) || '-';
                              })()}
                            </span>
                          </div>
                          <p className="text-sm whitespace-pre-wrap text-muted-foreground line-clamp-3">{note.content}</p>
                        </div>
                        {/* View/Edit/Delete Actions */}
                        <div className="flex gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setNoteToView(note);
                              setViewNoteDialogOpen(true);
                            }}
                            title="View note"
                            data-testid={`view-note-${note.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/case-notes/${note.id}/edit`)}
                            title="Edit note"
                            data-testid={`edit-note-${note.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {/* Request Review Button (only for reviewers, only for notes not by them) */}
                          {canRequestReview && note.author_id !== user?.id && !note.review_status && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openReviewDialog(note)}
                              title="Request review/correction"
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              data-testid={`request-review-${note.id}`}
                            >
                              <AlertCircle className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Highlight/Pin Button (only for authorized roles, only for non-highlighted notes) */}
                          {canHighlightNotes && !note.is_highlighted && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleHighlight(note, true)}
                              title="Highlight as pinned alert"
                              className="text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                              data-testid={`highlight-note-${note.id}`}
                              disabled={highlightLoading === note.id}
                            >
                              {highlightLoading === note.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Pin className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteNoteDialog(note)}
                            title="Delete note"
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-note-${note.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-3 pt-2 border-t border-border/50">
                        Created by {note.author_name} • {formatDateTime(note.created_at)}
                        {note.updated_at && note.updated_at !== note.created_at && (
                          <span> • Updated {formatDateTime(note.updated_at)}</span>
                        )}
                        {note.review_history?.length > 0 && (
                          <span> • {note.review_history.length} review(s)</span>
                        )}
                      </p>
                    </div>
                  );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No notes yet</p>
                  <Button className="mt-4" asChild>
                    <Link to={`/case-notes/new?case=${id}`}>Add First Note</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Participant Costs Tab - SNAP E&T Only */}
        {isSnapEtCase && (
          <TabsContent value="costs">
            <ParticipantCostsPanel caseId={id} caseData={caseData} />
          </TabsContent>
        )}

        {isSnapEtCase && (
          <TabsContent value="ssr-tickets">
            <SupportiveServiceTicketsTab caseId={id} />
          </TabsContent>
        )}

        <TabsContent value="timeline">
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="text-lg">Case Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              {caseData.timeline?.length > 0 ? (
                <div className="space-y-4">
                  {caseData.timeline.slice().reverse().map((event, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Clock className="w-4 h-4 text-primary" />
                        </div>
                        {index < caseData.timeline.length - 1 && (
                          <div className="w-0.5 h-full bg-border mt-2" />
                        )}
                      </div>
                      <div className="pb-4">
                        <p className="font-medium">{event.event}</p>
                        <p className="text-sm text-muted-foreground">
                          {event.user} • {formatDateTime(event.date)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No timeline events</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Appointments Tab */}
        <TabsContent value="appointments">
          <Card className="card-shadow">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CalendarDays className="w-5 h-5" />
                  Case Appointments
                </CardTitle>
                <CardDescription>
                  Schedule and manage appointments for this case
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchAppointments}
                  disabled={loadingAppointments}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingAppointments ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button
                  size="sm"
                  onClick={openNewAppointmentDialog}
                  data-testid="new-case-appointment-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Appointment
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingAppointments ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : appointments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No appointments scheduled</p>
                  <Button
                    variant="link"
                    onClick={openNewAppointmentDialog}
                    className="mt-2"
                  >
                    Schedule an appointment
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {appointments.map((apt) => {
                    const typeInfo = appointmentTypes.find(t => t.id === apt.appointment_type);
                    const isPast = new Date(`${apt.date}T${apt.end_time || apt.start_time}`) < new Date();
                    const isUpdating = updatingAppointment === apt.id;
                    
                    return (
                      <div 
                        key={apt.id} 
                        className={`p-4 border rounded-lg hover:bg-muted/50 transition-colors ${isPast ? 'opacity-70' : ''}`}
                        data-testid={`appointment-${apt.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1">
                            {/* Color indicator */}
                            <div 
                              className={`w-2 min-h-[50px] rounded-full flex-shrink-0`}
                              style={{ backgroundColor: typeInfo?.color || '#6b7280' }}
                            />
                            
                            <div className="flex-1">
                              {/* Title and badges */}
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <h4 className="font-medium">{apt.title}</h4>
                                <Badge className={getAppointmentStatusStyle(apt.status)}>
                                  {apt.status || 'Scheduled'}
                                </Badge>
                                {apt.case_id && apt.case_id === id && (
                                  <Badge variant="outline" className="text-xs">Case-linked</Badge>
                                )}
                              </div>
                              
                              {/* Date and time */}
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {formatDateUtil(apt.date)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {aptTimeLabel(apt)}
                                  {apt.participant_tz_missing && <span className="ml-1 text-[10px] text-amber-600" data-testid={`apt-tz-missing-${apt.id}`}>(participant's time zone isn't set)</span>}
                                </span>
                                {apt.location_type && (
                                  <span className="flex items-center gap-1">
                                    {getLocationIcon(apt.location_type)}
                                    {apt.location_type === 'in_person' ? 'In Person' : 
                                     apt.location_type === 'phone' ? 'Phone' : 'Video'}
                                  </span>
                                )}
                              </div>
                              
                              {/* Location details */}
                              {apt.location && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {apt.location}
                                </p>
                              )}
                              
                              {/* Case manager + recurring info */}
                              <div className="flex items-center gap-2 mt-2 flex-wrap text-xs text-muted-foreground" data-testid={`appointment-case-manager-${apt.id}`}>
                                <span>Case Manager:</span>
                                {(apt.status?.toLowerCase() === 'scheduled' || !apt.status) && !isPast ? (
                                  <AppointmentCaseManagerSelect
                                    value={apt.case_manager_id}
                                    staffList={staffList}
                                    disabled={isUpdating}
                                    onChange={(v) => handleAppointmentCaseManager(apt, v)}
                                    testId={`appointment-case-manager-select-${apt.id}`}
                                  />
                                ) : (
                                  <span className="font-medium text-foreground">{apt.case_manager_name || 'None'}</span>
                                )}
                                {apt.is_recurring && apt.series_id && (
                                  <Badge variant="outline" className="text-xs">Recurring #{apt.occurrence_number}</Badge>
                                )}
                              </div>

                              {/* Notes */}
                              {apt.notes && (
                                <p className="text-sm text-muted-foreground mt-2 italic">
                                  {apt.notes}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Action buttons - only show for scheduled appointments */}
                          {(apt.status?.toLowerCase() === 'scheduled' || !apt.status) && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {/* Edit */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setEditingAppointment(apt)}
                                disabled={isUpdating}
                                title="Edit"
                                data-testid={`edit-appointment-${apt.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>

                              {/* Reschedule */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openRescheduleDialog(apt)}
                                disabled={isUpdating}
                                title="Reschedule"
                                data-testid={`reschedule-${apt.id}`}
                              >
                                {isUpdating ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-4 h-4" />
                                )}
                              </Button>
                              
                              {/* No Show */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleUpdateAppointmentStatus(apt, 'No Show')}
                                disabled={isUpdating}
                                title="Mark as No Show"
                                className="text-orange-600 hover:text-orange-700"
                                data-testid={`no-show-${apt.id}`}
                              >
                                <AlertTriangle className="w-4 h-4" />
                              </Button>
                              
                              {/* Cancel */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to cancel this appointment?')) {
                                    handleUpdateAppointmentStatus(apt, 'Cancelled');
                                  }
                                }}
                                disabled={isUpdating}
                                title="Cancel"
                                className="text-red-600 hover:text-red-700"
                                data-testid={`cancel-${apt.id}`}
                              >
                                <Ban className="w-4 h-4" />
                              </Button>
                              
                              {/* Mark as Attended */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleUpdateAppointmentStatus(apt, 'Attended')}
                                disabled={isUpdating}
                                title="Mark as Attended"
                                className="text-green-600 hover:text-green-700"
                                data-testid={`attended-${apt.id}`}
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* View Note Dialog */}
      <Dialog open={viewNoteDialogOpen} onOpenChange={setViewNoteDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              View Case Note
            </DialogTitle>
          </DialogHeader>
          
          {noteToView && (
            <div className="space-y-4 py-4">
              {/* Subject */}
              <div>
                <h3 className="text-lg font-semibold">{noteToView.subject || 'Untitled Note'}</h3>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <Badge variant="outline" className="capitalize">{noteToView.note_type?.replace(/_/g, ' ')}</Badge>
                  <Badge className={`status-${noteToView.status}`}>{noteToView.status}</Badge>
                  {noteToView.is_system_generated && (
                    <Badge variant="secondary" className="text-xs">System Generated</Badge>
                  )}
                  {noteToView.is_sensitive && (
                    <Badge variant="destructive" className="text-xs">Sensitive</Badge>
                  )}
                </div>
              </div>
              
              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 p-3 rounded-lg bg-muted text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">Date of Service</p>
                  <p className="font-medium">
                    {formatDateUtil(noteToView.date_of_service) || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Created By</p>
                  <p className="font-medium">{noteToView.author_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Created At</p>
                  <p className="font-medium">{formatDateTime(noteToView.created_at)}</p>
                </div>
                {noteToView.updated_at && noteToView.updated_at !== noteToView.created_at && (
                  <div>
                    <p className="text-muted-foreground text-xs">Last Updated</p>
                    <p className="font-medium">{formatDateTime(noteToView.updated_at)}</p>
                  </div>
                )}
              </div>
              
              {/* Content */}
              <div>
                <Label className="text-xs text-muted-foreground">Note Content</Label>
                <div className="mt-2 p-4 rounded-lg border border-border bg-card">
                  <p className="whitespace-pre-wrap text-sm">{noteToView.content}</p>
                </div>
              </div>
              
              {/* Version History (if exists) */}
              {noteToView.version && noteToView.version > 1 && (
                <div className="text-xs text-muted-foreground">
                  Version {noteToView.version} • Edited {noteToView.version - 1} time(s)
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setViewNoteDialogOpen(false);
                setNoteToView(null);
              }}
            >
              Close
            </Button>
            <Button 
              onClick={() => {
                setViewNoteDialogOpen(false);
                navigate(`/case-notes/${noteToView.id}/edit`);
              }}
              data-testid="view-to-edit-btn"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Note Dialog */}
      <Dialog open={deleteNoteDialogOpen} onOpenChange={setDeleteNoteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete Case Note
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. The note will be removed from the case record.
            </DialogDescription>
          </DialogHeader>
          
          {noteToDelete && (
            <div className="space-y-4 py-4">
              {/* Note info */}
              <div className="p-3 rounded-lg bg-muted text-sm">
                <p className="font-medium">{noteToDelete.subject || 'Untitled Note'}</p>
                <p className="text-muted-foreground mt-1 line-clamp-2">{noteToDelete.content}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  Created by {noteToDelete.author_name} on {formatDateUtil(noteToDelete.created_at)}
                </p>
              </div>
              
              {/* System-generated warning */}
              {noteToDelete.is_system_generated && (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <div className="flex gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800 dark:text-amber-200">
                      This is a system-generated note from an automated process. Deleting it may affect audit records.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Deletion reason */}
              <div className="space-y-2">
                <Label htmlFor="delete-reason">
                  Reason for Deletion <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="delete-reason"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Please provide a reason for deleting this note..."
                  rows={3}
                  data-testid="delete-reason-input"
                />
                <p className="text-xs text-muted-foreground">
                  This reason will be recorded in the audit log.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setDeleteNoteDialogOpen(false);
                setNoteToDelete(null);
                setDeleteReason('');
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteNote}
              disabled={deletingNote || !deleteReason.trim()}
              data-testid="confirm-delete-note-btn"
            >
              {deletingNote ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Note
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              Request Case Note Review
            </DialogTitle>
            <DialogDescription>
              Request the author to review and correct this case note. They will be notified via in-app notification and SMS.
            </DialogDescription>
          </DialogHeader>
          
          {noteToReview && (
            <div className="space-y-4 py-4">
              {/* Note info */}
              <div className="p-3 rounded-lg bg-muted text-sm">
                <p className="font-medium">{noteToReview.subject || 'Untitled Note'}</p>
                <p className="text-muted-foreground mt-1 line-clamp-2">{noteToReview.content}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  Created by {noteToReview.author_name} on {formatDateUtil(noteToReview.created_at)}
                </p>
              </div>
              
              {/* Review comment */}
              <div className="space-y-2">
                <Label htmlFor="review-comment">
                  What needs to be corrected? <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="review-comment"
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="Describe what needs to be corrected or improved in this case note..."
                  rows={4}
                  data-testid="review-comment-input"
                />
                <p className="text-xs text-muted-foreground">
                  This comment will be shown to the author and recorded in the review history.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setReviewDialogOpen(false);
                setNoteToReview(null);
                setReviewComment('');
              }}
            >
              Cancel
            </Button>
            <Button 
              className="bg-orange-600 hover:bg-orange-700"
              onClick={handleRequestReview}
              disabled={requestingReview || !reviewComment.trim()}
              data-testid="confirm-request-review-btn"
            >
              {requestingReview ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Requesting...
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Request Review
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit Correction Dialog */}
      <Dialog open={correctionDialogOpen} onOpenChange={setCorrectionDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCheck className="w-5 h-5 text-green-500" />
              Submit Correction
            </DialogTitle>
            <DialogDescription>
              Confirm that you have made the necessary corrections to this case note. The reviewer will be notified.
            </DialogDescription>
          </DialogHeader>
          
          {noteToCorrected && (
            <div className="space-y-4 py-4">
              {/* Review info */}
              <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 text-sm">
                <p className="font-medium text-orange-800 dark:text-orange-300">Review Comment:</p>
                <p className="text-orange-700 dark:text-orange-400 mt-1">{noteToCorrected.current_review?.comment}</p>
                <p className="text-xs text-orange-600 dark:text-orange-500 mt-2">
                  Requested by {noteToCorrected.current_review?.reviewer_name}
                </p>
              </div>
              
              {/* Correction notes (optional) */}
              <div className="space-y-2">
                <Label htmlFor="correction-notes">
                  Correction Notes (optional)
                </Label>
                <Textarea
                  id="correction-notes"
                  value={correctionNotes}
                  onChange={(e) => setCorrectionNotes(e.target.value)}
                  placeholder="Optionally describe what changes you made..."
                  rows={3}
                  data-testid="correction-notes-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setCorrectionDialogOpen(false);
                setNoteToCorrected(null);
                setCorrectionNotes('');
              }}
            >
              Cancel
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={handleSubmitCorrection}
              disabled={submittingCorrection}
              data-testid="confirm-submit-correction-btn"
            >
              {submittingCorrection ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCheck className="w-4 h-4 mr-2" />
                  Submit Correction
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* MTAJ-NC Reverse Referral Outcome Dialog */}
      <Dialog open={mtajReverseReferralDialogOpen} onOpenChange={setMtajReverseReferralDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reverse Referral Outcome</DialogTitle>
            <DialogDescription>
              Please select the outcome of the reverse referral before creating the case note.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <label className="text-sm font-medium">Select Referral Outcome *</label>
              <RadioGroup value={mtajOutcome} onValueChange={setMtajOutcome}>
                <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="approved" id="outcome-approved" />
                  <Label htmlFor="outcome-approved" className="flex-1 cursor-pointer">
                    <span className="font-medium text-green-700">Approved</span>
                    <p className="text-sm text-muted-foreground">The referral was approved to proceed with enrollment and service delivery.</p>
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="denied" id="outcome-denied" />
                  <Label htmlFor="outcome-denied" className="flex-1 cursor-pointer">
                    <span className="font-medium text-red-700">Denied</span>
                    <p className="text-sm text-muted-foreground">The referral was denied to proceed with enrollment and service delivery.</p>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMtajReverseReferralDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleCreateMtajQuickNote('mtaj_reverse_referral', mtajOutcome)}
              disabled={!mtajOutcome || creatingMtajQuickNote}
              data-testid="submit-reverse-referral-outcome"
            >
              {creatingMtajQuickNote ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Case Note'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Appointment Dialog */}
      <Dialog open={showNewAppointmentDialog} onOpenChange={setShowNewAppointmentDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Schedule Appointment</DialogTitle>
            <DialogDescription>
              Create a new appointment for {participant?.full_name || 'this participant'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="apt-title">Title *</Label>
              <Input
                id="apt-title"
                placeholder="Appointment title"
                value={newAppointment.title}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, title: e.target.value }))}
                data-testid="case-appointment-title-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={newAppointment.appointment_type}
                  onValueChange={handleAppointmentTypeChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {appointmentTypes.map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.label || type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Location Type</Label>
                <Select
                  value={newAppointment.location_type}
                  onValueChange={(v) => setNewAppointment(prev => ({ ...prev, location_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_person">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" /> In Person
                      </div>
                    </SelectItem>
                    <SelectItem value="phone">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4" /> Phone Call
                      </div>
                    </SelectItem>
                    <SelectItem value="video">
                      <div className="flex items-center gap-2">
                        <Video className="w-4 h-4" /> Video Call
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={newAppointment.date}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Input
                  type="time"
                  value={newAppointment.start_time}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, start_time: e.target.value }))}
                />
                <ParticipantSeesLine participantId={caseData?.participant_id} date={newAppointment.date} time={newAppointment.start_time} />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={newAppointment.end_time}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, end_time: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Location / Meeting Link</Label>
              <Input
                placeholder={newAppointment.location_type === 'video' ? 'Teams/Zoom link' : 'Address or room'}
                value={newAppointment.location}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, location: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Additional notes..."
                value={newAppointment.notes}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Case Manager</Label>
              <AppointmentCaseManagerSelect
                value={newAppointment.case_manager_id}
                staffList={staffList}
                onChange={(v) => setNewAppointment(prev => ({ ...prev, case_manager_id: v }))}
                testId="case-appointment-case-manager-select"
              />
              <p className="text-xs text-muted-foreground">Defaults to the case's assigned case manager.</p>
            </div>

            {appointmentTypes.find(t => t.id === newAppointment.appointment_type)?.triggers_snap_et_reminder && (
              <CaseAppointmentRecurring value={newAppointment} onChange={(patch) => setNewAppointment(prev => ({ ...prev, ...patch }))} />
            )}

            {/* Info about case linking */}
            <div className="p-3 rounded-lg bg-muted text-sm">
              <p className="text-muted-foreground">
                This appointment will be linked to case <span className="font-mono">{id}</span> and participant <span className="font-medium">{participant?.full_name}</span>.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewAppointmentDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateAppointment} disabled={savingAppointment}>
              {savingAppointment && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {newAppointment.is_recurring ? 'Create Recurring Appointments' : 'Create Appointment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AppointmentEditDialog
        appointment={editingAppointment}
        onClose={() => setEditingAppointment(null)}
        onSaved={() => { setEditingAppointment(null); fetchAppointments(); }}
      />

      {/* Reschedule Appointment Dialog */}
      <Dialog open={showRescheduleDialog} onOpenChange={setShowRescheduleDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reschedule Appointment</DialogTitle>
            <DialogDescription>
              {appointmentToReschedule && (
                <>
                  Rescheduling: <strong>{appointmentToReschedule.title}</strong> from {formatDateUtil(appointmentToReschedule.date)} at {aptTimeLabel(appointmentToReschedule)}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Date *</Label>
              <Input
                type="date"
                value={rescheduleDate}
                onChange={(e) => setRescheduleDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                data-testid="reschedule-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>New Time *</Label>
              <Input
                type="time"
                value={rescheduleTime}
                onChange={(e) => setRescheduleTime(e.target.value)}
                data-testid="reschedule-time-input"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              The original appointment will be marked as "Rescheduled" and a new appointment will be created.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRescheduleDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleUpdateAppointmentStatus(
                appointmentToReschedule, 
                'Rescheduled', 
                { rescheduled_to_date: rescheduleDate, rescheduled_to_time: rescheduleTime }
              )} 
              disabled={!rescheduleDate || !rescheduleTime || updatingAppointment}
            >
              {updatingAppointment && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reschedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EnrollmentSummaryQuestionsDialog
        open={!!pendingSummaryRun}
        caseId={id}
        participantId={caseData?.participant_id}
        onComplete={(summary) => { const run = pendingSummaryRun; setPendingSummaryRun(null); run && run(summary); }}
        onCancel={() => setPendingSummaryRun(null)}
      />
    </div>
  );
};

export default CaseDetailPage;
